import { useState } from 'react';
import { formatMoney, type ConsentRequest } from '@teentack/shared';
import { useConsentQueue, useDecideConsent } from '@/features/consent/use-consent';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorNote } from '@/components/Form';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { BadgeCheckIcon, MapPinIcon, WalletIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';
import { TrustBadge } from '@/features/connections/TrustBadge';

const WHEN = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatWhen(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : WHEN.format(date);
}

/**
 * The guardian decides, per job. [Parent steps 9-10]
 *
 * This is the second gate in the product: registration approval lets a teen in,
 * and this one decides which specific work they may apply for.
 */
export function ConsentRequestsPage() {
  const [status, setStatus] = useState<'PENDING' | 'APPROVED' | 'DECLINED'>('PENDING');
  const queue = useConsentQueue({ status, pageSize: 50 });
  const decide = useDecideConsent();

  const [declining, setDeclining] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function respond(request: ConsentRequest, decision: 'APPROVE' | 'DECLINE'): Promise<void> {
    setError(null);
    try {
      await decide.mutateAsync({
        consentId: request.id,
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setDeclining(null);
      setNote('');
    } catch (caught) {
      setError(presentApiError(caught, 'Could not record that decision').message);
    }
  }

  const page = queue.data;

  return (
    <ParentShell
      title="Job approvals"
      description="Your teen cannot apply for any job until you approve that specific job. Read what the work involves and who is offering it before you decide."
    >
      <div className="mb-6 flex gap-2">
        {(['PENDING', 'APPROVED', 'DECLINED'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
            className={`rounded-lg px-3.5 py-2 text-xs font-bold transition ${
              status === value
                ? 'bg-ink text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {value.charAt(0) + value.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <ErrorNote message={error} />

      {queue.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      )}

      {queue.isError && (
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <p className="text-sm text-slate-500">
            {presentApiError(queue.error, 'Could not load requests').message}
          </p>
          <Button className="mt-5" onClick={() => void queue.refetch()}>
            Try again
          </Button>
        </div>
      )}

      {page && page.items.length === 0 && (
        <EmptyState
          icon={<BadgeCheckIcon className="size-6" />}
          title={
            status === 'PENDING' ? 'Nothing waiting on you' : `No ${status.toLowerCase()} requests`
          }
        >
          {status === 'PENDING'
            ? 'When your teen finds a job they want, their request to apply appears here.'
            : 'Requests you have answered show up under this tab.'}
        </EmptyState>
      )}

      {page && page.items.length > 0 && (
        <ul className="space-y-4">
          {page.items.map((request) => (
            <li
              key={request.id}
              className="rounded-2xl border border-line bg-white p-5 shadow-soft sm:p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-bold tracking-wide text-slate-400 uppercase">
                    {request.teen.name} wants to apply for
                  </p>
                  <h2 className="mt-1 text-base font-bold text-ink">{request.job.title}</h2>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="font-semibold text-slate-600">{request.job.posterName}</span>
                    {request.job.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPinIcon className="size-3.5 text-slate-400" />
                        {request.job.location}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 font-bold text-ink">
                      <WalletIcon className="size-3.5 text-safe-600" />
                      {formatMoney(request.job.pay.amountMinor, request.job.pay.currency)}
                    </span>
                    <span>Asked {formatWhen(request.requestedAt)}</span>
                  </p>
                </div>

                {request.status === 'PENDING' ? (
                  declining !== request.id && (
                    <div className="flex w-full gap-2 sm:w-auto">
                      <Button
                        variant="success"
                        disabled={decide.isPending}
                        onClick={() => void respond(request, 'APPROVE')}
                        className="flex-1 sm:flex-none"
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        disabled={decide.isPending}
                        onClick={() => setDeclining(request.id)}
                        className="flex-1 sm:flex-none"
                      >
                        Decline
                      </Button>
                    </div>
                  )
                ) : (
                  <Badge tone={request.status === 'APPROVED' ? 'safe' : 'neutral'}>
                    {request.status.charAt(0) + request.status.slice(1).toLowerCase()}
                  </Badge>
                )}
              </div>

              <div className="mt-4">
                <TrustBadge trust={request.trust} tone="block" />
              </div>

              {declining === request.id && (
                <div className="mt-4 rounded-xl border border-sun-200 bg-sun-50 p-4">
                  <Textarea
                    label={`Why are you declining this for ${request.teen.name}?`}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    optional
                    maxLength={500}
                    placeholder="They will see this, so a short reason helps."
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="outline"
                      disabled={decide.isPending}
                      onClick={() => void respond(request, 'DECLINE')}
                    >
                      {decide.isPending ? 'Working…' : 'Confirm decline'}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={decide.isPending}
                      onClick={() => {
                        setDeclining(null);
                        setNote('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </ParentShell>
  );
}
